import { z } from 'zod';

/**
 * Coarse viewport presets that drive the segmented "Run as
 * Desktop / Tablet / Mobile" toggle on flow creation and in the
 * run-config drawer. Intentionally tiny — three rows — so that
 * picking a default is a single click. Finer-grained device
 * emulation (specific phones, network throttling, orientation)
 * still lives in `MobileEmulationConfig` and `DEVICE_PROFILES`.
 *
 * Stored on `test_cases.default_viewport_preset_id` as free-form
 * TEXT and validated against `VIEWPORT_PRESET_IDS` in the API
 * layer (same pattern as `test_runs.device`). NULL is allowed
 * and means "no opinion" — the launcher resolves it to
 * `desktop` (the historical implicit default).
 *
 * Authority: `@rynora/contracts/viewport-presets.ts`. The
 * flow-builder UI may map these ids to richer in-app device
 * profiles (e.g. `mobile` → an iPhone 15 stub) but the wire
 * value is always one of these three ids.
 */

export const VIEWPORT_PRESET_IDS = ['desktop', 'tablet', 'mobile'] as const;
export type ViewportPresetId = (typeof VIEWPORT_PRESET_IDS)[number];

export const ViewportPresetIdSchema = z.enum(VIEWPORT_PRESET_IDS);

export const ViewportPresetSchema = z.object({
  id: ViewportPresetIdSchema,
  /** Short human label for the segmented control. */
  label: z.string().min(1).max(32),
  /** Representative viewport — picker UI hint only, runner derives the real one. */
  viewport: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
});
export type ViewportPreset = z.infer<typeof ViewportPresetSchema>;

/**
 * Curated preset registry. The viewports are the "headline"
 * dimensions shown in the picker; the runner is free to substitute
 * a more specific device when the per-flow `MobileEmulationConfig`
 * overrides it.
 *
 *   - desktop → 1440×900 (laptop-typical, matches Playwright default `viewport`)
 *   - tablet  → 820×1180 (iPad Air, the median tablet)
 *   - mobile  → 393×852 (iPhone 15, the median modern phone)
 */
export const VIEWPORT_PRESETS: Record<ViewportPresetId, ViewportPreset> = {
  desktop: {
    id: 'desktop',
    label: 'Desktop',
    viewport: { width: 1440, height: 900 },
  },
  tablet: {
    id: 'tablet',
    label: 'Tablet',
    viewport: { width: 820, height: 1180 },
  },
  mobile: {
    id: 'mobile',
    label: 'Mobile',
    viewport: { width: 393, height: 852 },
  },
};

export function findViewportPreset(id: string | null | undefined): ViewportPreset | null {
  if (!id) return null;
  if ((VIEWPORT_PRESET_IDS as readonly string[]).includes(id)) {
    return VIEWPORT_PRESETS[id as ViewportPresetId];
  }
  return null;
}

/**
 * Default the launcher / picker falls back to when a flow row's
 * `default_viewport_preset_id` is NULL. Centralised here so the
 * launcher, the run-config drawer, and the run-attempt header
 * agree without re-deriving the constant.
 */
export const DEFAULT_VIEWPORT_PRESET_ID: ViewportPresetId = 'desktop';

export function resolveViewportPreset(id: string | null | undefined): ViewportPreset {
  return findViewportPreset(id) ?? VIEWPORT_PRESETS[DEFAULT_VIEWPORT_PRESET_ID];
}
